import { AlertCircle, ArrowLeft, ArrowRight, Building2, Globe2, Landmark, Plus, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckboxField } from "@/shared/components/forms/ColocaCheckboxField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings, PERCENTAGE_INPUT_PATTERN } from "@/shared/utils/decimal";
import { formatTaxLabel } from "@/shared/utils/paymentTaxes";

import { countryLabel } from "../../constants/paymentTaxes.constants";
import { useFeeLabels } from "../../hooks/useFeeLabels";
import { useTaxMutations } from "../../hooks/useTaxMutations";
import type { CorridorCoverage, ProviderRailRoute } from "../../types/paymentFees.types";
import type { PaymentTax } from "../../types/paymentTaxes.types";
import { corridorLabel, formatAmountText } from "../../utils/feeFormat";
import {
  candidateTax,
  corridorTaxAmounts,
  routesWonBy,
  scopeOfRoute,
  taxEditTarget,
  type TaxCorridorCoverage,
} from "../../utils/taxCoverage";
import { resolveTaxes, resolveTaxWinners, taxScopeKey } from "../../utils/taxMath";
import { CorridorIdentity } from "../fees/CorridorIdentity";
import {
  buildCorridorTaxSchema,
  CORRIDOR_TAX_STEP_FIELDS,
  corridorTaxPayload,
  seedCorridorTax,
  validFromOf,
  type CorridorTaxFormValues,
} from "./corridorTaxFormSchema";
import { scopeLabel } from "./taxScopeLabels";

export interface EditCorridorTaxIntent {
  coverage: TaxCorridorCoverage;
  /** Writes that organization's own rate; null writes the general one. */
  organizationId: string | null;
  organizationName: string | null;
}

export interface EditCorridorTaxDialogProps {
  intent: EditCorridorTaxIntent;
  onClose: () => void;
  onSaved: (tax: PaymentTax, validFrom: string | null) => void;
  /** The country has no tax yet: hand over to the new tax dialog. */
  onCreateTax: () => void;
  rows: PaymentTax[];
  routes: ProviderRailRoute[];
  /** The price book read in the same scope, for every corridor's example amount and commission. */
  corridors: CorridorCoverage[];
  now: number;
  organizationNames: Record<string, string>;
}

const FORM_ID = "edit-corridor-tax-form";

/** Templates of the country's taxes, one per code: the latest general version, else any. */
function countryTaxes(rows: PaymentTax[], country: string): PaymentTax[] {
  const byCode = new Map<string, PaymentTax>();
  const ordered = [...rows]
    .filter((tax) => tax.country === country)
    .sort(
      (a, b) =>
        Number(!a.organization_id) - Number(!b.organization_id) ||
        new Date(a.valid_from).getTime() - new Date(b.valid_from).getTime(),
    );
  for (const tax of ordered) byCode.set(tax.code, tax);
  return [...byCode.values()].sort((a, b) =>
    a.base === b.base ? a.code.localeCompare(b.code) : a.base === "COMMISSION" ? -1 : 1,
  );
}

export function EditCorridorTaxDialog({
  intent,
  onClose,
  onSaved,
  onCreateTax,
  rows,
  routes,
  corridors,
  now,
  organizationNames,
}: EditCorridorTaxDialogProps) {
  const { t, activeLanguage } = useTranslations("paymentTaxes");
  const labels = useFeeLabels();
  const { isSubmitting, error, createTax } = useTaxMutations();
  const [step, setStep] = useState<"form" | "review">("form");

  const { coverage, organizationId, organizationName } = intent;
  const route = coverage.corridor.route;
  const isOwn = organizationId != null;
  const requiresAck = !isOwn;
  const name = organizationName ?? organizationId ?? "";
  const currency = route.currency;
  const templates = useMemo(() => countryTaxes(rows, route.country), [rows, route.country]);
  const corridorOf = useMemo(
    () => new Map(corridors.map((corridor) => [corridor.route.id, corridor])),
    [corridors],
  );

  const firstCode = coverage.lines[0]?.tax.code ?? templates[0]?.code ?? "";
  const targetFor = (code: string) => taxEditTarget(route, code, organizationId, rows, routes, now);
  const seedFor = (code: string) => {
    const target = targetFor(code);
    const generalToday =
      resolveTaxWinners(rows, scopeOfRoute(route, null), now).find((tax) => tax.code === code) ?? null;
    return seedCorridorTax(
      code,
      target.effective ?? generalToday,
      templates.find((tax) => tax.code === code) ?? null,
      target.broad ? "BROAD" : "CORRIDOR",
      requiresAck,
    );
  };

  const schema = useMemo(
    () =>
      buildCorridorTaxSchema({
        rate: t("validation_rate"),
        rateGross: t("validation_rate_gross"),
        validFrom: t("validation_valid_from"),
        reason: t("validation_reason"),
        acknowledge: t("validation_ack"),
      }),
    [t],
  );
  const form = useZodForm(schema, seedFor(firstCode));
  const values = useWatch({ control: form.control }) as CorridorTaxFormValues;

  const [seededCode, setSeededCode] = useState(firstCode);
  useEffect(() => {
    if (!values.code || values.code === seededCode) return;
    setSeededCode(values.code);
    form.reset(seedFor(values.code));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.code]);

  const template = templates.find((tax) => tax.code === values.code) ?? null;
  const target = useMemo(
    () => (values.code ? taxEditTarget(route, values.code, organizationId, rows, routes, now) : null),
    [route, values.code, organizationId, rows, routes, now],
  );
  const writesBroad = values.target === "BROAD" && target?.broad != null;
  const writeScope = writesBroad && target?.broad ? target.broad.scope : target?.corridorScope;
  const validFromIso = validFromOf(values);
  const rateIsValid = values.exempt || PERCENTAGE_INPUT_PATTERN.test((values.rate ?? "").trim());

  const candidate =
    template && writeScope && rateIsValid
      ? candidateTax(template, {
          rate: values.exempt ? "0" : values.rate.trim(),
          bearer: values.bearer,
          organization_id: organizationId,
          rail: writeScope.rail,
          direction: writeScope.direction,
          valid_from: validFromIso ?? new Date(now).toISOString(),
        })
      : null;
  const hasCollision =
    candidate != null &&
    validFromIso != null &&
    rows.some(
      (tax) =>
        taxScopeKey(tax) === taxScopeKey(candidate) &&
        new Date(tax.valid_from).getTime() === new Date(validFromIso).getTime(),
    );

  /* --------------------------- today vs with the change --------------------------- */

  const appliesAt = candidate ? Math.max(now, new Date(candidate.valid_from).getTime()) : now;
  const amountsOn = (corridor: CorridorCoverage, taxRows: PaymentTax[], at: number) =>
    corridorTaxAmounts(corridor, resolveTaxes(taxRows, scopeOfRoute(corridor.route, organizationId), at));
  const codeAmount = (corridor: CorridorCoverage, taxRows: PaymentTax[], at: number) =>
    amountsOn(corridor, taxRows, at).lines.find((line) => line.tax.code === values.code)?.amount ?? null;

  const withCandidate = candidate ? [...rows, candidate] : rows;
  const today = amountsOn(coverage.corridor, rows, now);
  const next = amountsOn(coverage.corridor, withCandidate, appliesAt);
  const comparison = [
    {
      id: "tax",
      label: template?.name ?? t("example_tax_fallback"),
      today: codeAmount(coverage.corridor, rows, now),
      next: codeAmount(coverage.corridor, withCandidate, appliesAt),
    },
    { id: "client", label: t("corridors_th_client"), today: today.clientTotal, next: next.clientTotal },
    { id: "coloca", label: t("corridors_th_coloca"), today: today.colocaTotal, next: next.colocaTotal },
  ];

  /* ---------------------------------- review ---------------------------------- */

  const affected = candidate ? routesWonBy(candidate, rows, routes, now) : [];
  const reviewRows = affected.flatMap((affectedRoute) => {
    const corridor = corridorOf.get(affectedRoute.id);
    if (!corridor) return [];
    return [
      {
        route: affectedRoute,
        amount: corridor.exampleAmount,
        before: codeAmount(corridor, rows, now),
        after: codeAmount(corridor, withCandidate, appliesAt),
      },
    ];
  });
  const keptOrganizations = isOwn
    ? []
    : [
        ...new Set(
          rows
            .filter(
              (tax) =>
                tax.organization_id &&
                tax.code === values.code &&
                affected.some((affectedRoute) =>
                  resolveTaxWinners(rows, scopeOfRoute(affectedRoute, tax.organization_id), now).some(
                    (winner) => winner.id === tax.id,
                  ),
                ),
            )
            .map((tax) => tax.organization_id as string),
        ),
      ];

  /* ---------------------------------- submit ---------------------------------- */

  const goToReview = async () => {
    const valid = await form.trigger([...CORRIDOR_TAX_STEP_FIELDS]);
    if (!valid || hasCollision) return;
    form.clearErrors(["reason", "acknowledged"]);
    setStep("review");
  };

  const handleSubmit = async (submitted: CorridorTaxFormValues) => {
    if (hasCollision || !template || !writeScope) return;
    const created = await createTax(corridorTaxPayload(submitted, template, writeScope, organizationId));
    if (created) onSaved(created, validFromOf(submitted));
  };

  /* ---------------------------------- render ---------------------------------- */

  const corridorText = corridorLabel(route, labels.directionShort);
  const money = (value: string | null) =>
    value == null ? "—" : `${formatAmountText(value, activeLanguage)} ${currency}`;
  const whereOf = (scope: {
    rail: ProviderRailRoute["rail"] | null;
    direction: ProviderRailRoute["direction"] | null;
  }) => `${countryLabel(route.country, activeLanguage)} · ${scopeLabel(scope, t)}`;
  const rateText = (tax: PaymentTax | null) =>
    !tax
      ? t("review_none")
      : compareDecimalStrings(tax.rate, "0") === 0
        ? t("exempt")
        : formatTaxLabel(tax, activeLanguage);

  const toolbar = (
    <div className="space-y-2">
      <CorridorIdentity route={route} />
      <div
        className={cn(
          "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm",
          isOwn ? "bg-primary/10 text-primary" : "bg-muted text-foreground",
        )}
      >
        {isOwn ? (
          <Building2 className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <Globe2 className="size-4 shrink-0" aria-hidden="true" />
        )}
        <span className="font-semibold">
          {isOwn ? t("edit_applies_own", { name }) : t("edit_applies_general")}
        </span>
      </div>
    </div>
  );

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-end gap-2">
      {step === "review" ? (
        <ColocaButton
          type="button"
          variant="ghost"
          icon={ArrowLeft}
          onClick={() => setStep("form")}
          disabled={isSubmitting}
        >
          {t("button_back")}
        </ColocaButton>
      ) : (
        <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
          {t("button_cancel")}
        </ColocaButton>
      )}
      {templates.length > 0 &&
        (requiresAck && step === "form" ? (
          <ColocaButton
            key="review"
            type="button"
            variant="primary"
            iconTrailing={ArrowRight}
            onClick={() => void goToReview()}
            disabled={hasCollision}
          >
            {t("button_review")}
          </ColocaButton>
        ) : (
          <ColocaButton
            key="save"
            type="submit"
            form={FORM_ID}
            variant="primary"
            icon={Save}
            loading={isSubmitting}
            disabled={hasCollision}
          >
            {t("button_save")}
          </ColocaButton>
        ))}
    </div>
  );

  return (
    <ColocaDialog
      open
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose();
      }}
      title={
        step === "review" ? t("review_title") : t(isOwn ? "edit_title_own" : "edit_title_general", { name })
      }
      description={corridorText}
      toolbar={toolbar}
      footer={footer}
      size="xl"
      mobileFullscreen
    >
      {templates.length === 0 ? (
        <ColocaEmpty
          icon={Landmark}
          title={t("edit_no_taxes_title", { country: countryLabel(route.country, activeLanguage) })}
          description={t("edit_no_taxes_description")}
          surface="card"
        >
          <ColocaButton variant="primary" size="sm" icon={Plus} onClick={onCreateTax}>
            {t("new_tax_button")}
          </ColocaButton>
        </ColocaEmpty>
      ) : (
        <ColocaForm id={FORM_ID} form={form} onSubmit={handleSubmit}>
          {step === "form" ? (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_19rem]">
              <div className="min-w-0 space-y-5">
                <ColocaSelectField
                  name="code"
                  label={t("edit_tax_label")}
                  items={templates.map((tax) => ({
                    value: tax.code,
                    label: `${tax.name} · ${t(tax.base === "COMMISSION" ? "base_commission" : "base_gross")}`,
                  }))}
                />

                {target?.broad && (
                  <ColocaRadioGroupField
                    name="target"
                    label={t("edit_target_label")}
                    items={[
                      {
                        value: "BROAD",
                        label: t("edit_target_broad", {
                          where: whereOf(target.broad.scope),
                          count: target.broad.routes.length,
                        }),
                      },
                      { value: "CORRIDOR", label: t("edit_target_corridor") },
                    ]}
                  />
                )}

                <label className="flex items-center gap-2.5 text-sm font-medium">
                  <ColocaSwitch
                    checked={values.exempt ?? false}
                    onCheckedChange={(checked) => form.setValue("exempt", checked, { shouldValidate: true })}
                    aria-label={t("edit_exempt")}
                  />
                  {t("edit_exempt")}
                </label>

                <div className="grid items-start gap-4 sm:grid-cols-2">
                  {!values.exempt && (
                    <ColocaAmountInput
                      name="rate"
                      label={t("field_rate")}
                      description={t(
                        values.base === "GROSS_AMOUNT" ? "field_rate_gross_hint" : "field_rate_hint",
                      )}
                      suffix="%"
                      maxFractionDigits={4}
                    />
                  )}
                  <ColocaRadioGroupField
                    name="bearer"
                    label={t("field_bearer")}
                    description={t(values.bearer === "COLOCA" ? "bearer_coloca_hint" : "bearer_client_hint")}
                    items={[
                      { value: "CLIENT", label: t("bearer_client") },
                      { value: "COLOCA", label: t("bearer_coloca") },
                    ]}
                  />
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-2.5 text-sm font-medium">
                    <ColocaSwitch
                      checked={values.effectiveNow ?? true}
                      onCheckedChange={(checked) => form.setValue("effectiveNow", checked)}
                      aria-label={t("eff_now_label")}
                    />
                    {t("eff_now_label")}
                  </label>
                  {!values.effectiveNow && (
                    <div className="space-y-1.5">
                      <div className="flex flex-wrap items-start gap-2">
                        <div className="w-40">
                          <ColocaInputField name="validFromDate" type="date" label={t("eff_date_label")} />
                        </div>
                        <div className="w-32">
                          <ColocaInputField name="validFromTime" type="time" label={t("eff_time_label")} />
                        </div>
                      </div>
                      {validFromIso && (
                        <p className="text-xs text-muted-foreground">
                          {t("edit_valid_from", {
                            date: formatDateTime(validFromIso, { locale: activeLanguage }),
                          })}
                        </p>
                      )}
                    </div>
                  )}
                  {hasCollision && (
                    <ColocaAlert variant="destructive" icon={AlertCircle} text={t("edit_collision")} />
                  )}
                </div>

                {!requiresAck && (
                  <ColocaTextareaField
                    name="reason"
                    label={t("reason_label")}
                    placeholder={t("reason_placeholder")}
                    description={t("reason_hint")}
                    maxLength={500}
                    rows={2}
                  />
                )}
                {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
              </div>

              <aside
                className="space-y-3 rounded-xl border border-border bg-muted/40 p-4 lg:self-start"
                aria-live="polite"
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("edit_compare_title")}
                </p>
                <p className="text-sm">
                  {t("edit_compare_amount", {
                    amount: `${formatAmountText(coverage.corridor.exampleAmount, activeLanguage)} ${currency}`,
                  })}
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] text-muted-foreground">
                      <th className="pb-1 text-left font-medium" />
                      <th className="pb-1 text-right font-medium">{t("review_before")}</th>
                      <th className="pb-1 text-right font-medium">{t("review_after")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.map((row) => (
                      <tr key={row.id} className={cn(row.id === "client" && "border-t border-border")}>
                        <td className="py-1 pr-2 text-xs text-muted-foreground">{row.label}</td>
                        <td className="py-1 text-right font-mono text-xs tabular-nums">{money(row.today)}</td>
                        <td
                          className={cn(
                            "py-1 text-right font-mono text-xs font-semibold tabular-nums",
                            row.next !== row.today && "text-primary",
                          )}
                        >
                          {money(row.next)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="space-y-1 border-t border-border pt-2 text-xs">
                  <p className="text-muted-foreground">{t("review_before")}</p>
                  <p className="font-mono">
                    {rateText(
                      resolveTaxWinners(rows, scopeOfRoute(route, organizationId), now).find(
                        (tax) => tax.code === values.code,
                      ) ?? null,
                    )}
                  </p>
                  {coverage.corridor.commissionAmount == null && values.base === "COMMISSION" && (
                    <p className="text-warning">{t("commission_unknown")}</p>
                  )}
                </div>
              </aside>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm">{t("review_intro", { count: reviewRows.length })}</p>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[28rem] text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t("corridors_th_corridor")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("review_before")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("review_after")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewRows.map((row) => (
                      <tr key={row.route.id} className="border-t border-border">
                        <td className="px-3 py-2">
                          <CorridorIdentity route={row.route} className="mb-0.5" />
                          <span className="block text-[11px] text-muted-foreground">
                            {t("edit_compare_amount", {
                              amount: `${formatAmountText(row.amount, activeLanguage)} ${row.route.currency}`,
                            })}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono tabular-nums">
                          {row.before == null
                            ? "—"
                            : `${formatAmountText(row.before, activeLanguage)} ${row.route.currency}`}
                        </td>
                        <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-primary">
                          {row.after == null
                            ? "—"
                            : `${formatAmountText(row.after, activeLanguage)} ${row.route.currency}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {keptOrganizations.length > 0 ? (
                <div className="space-y-2 text-sm">
                  <p>{t("review_orgs_kept", { count: keptOrganizations.length })}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {keptOrganizations.map((id) => (
                      <ColocaBadge key={id} variant="primary" className="font-sans">
                        {organizationNames[id] ?? id}
                      </ColocaBadge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("review_orgs_none")}</p>
              )}

              <ColocaTextareaField
                name="reason"
                label={t("reason_label")}
                placeholder={t("reason_placeholder")}
                description={t("reason_hint")}
                maxLength={500}
                rows={2}
              />
              <ColocaCheckboxField name="acknowledged" label={t("review_ack")} />
              {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
            </div>
          )}
        </ColocaForm>
      )}
    </ColocaDialog>
  );
}
